package org.moxultimate.topwithmox.wallpaper.details

import androidx.compose.runtime.*
import com.varabyte.kobweb.compose.css.Overflow
import com.varabyte.kobweb.compose.css.functions.clamp
import com.varabyte.kobweb.compose.foundation.layout.Arrangement
import com.varabyte.kobweb.compose.foundation.layout.Box
import com.varabyte.kobweb.compose.foundation.layout.Column
import com.varabyte.kobweb.compose.foundation.layout.Row
import com.varabyte.kobweb.compose.ui.Alignment
import com.varabyte.kobweb.compose.ui.Modifier
import com.varabyte.kobweb.compose.ui.modifiers.*
import com.varabyte.kobweb.framework.annotations.DelicateApi
import com.varabyte.kobweb.silk.style.breakpoint.Breakpoint
import com.varabyte.kobweb.silk.theme.breakpoint.rememberBreakpoint
import kotlinx.browser.document
import kotlinx.browser.window
import kotlinx.coroutines.await
import kotlinx.coroutines.launch
import org.jetbrains.compose.web.css.Color
import org.jetbrains.compose.web.css.FlexWrap
import org.jetbrains.compose.web.css.px
import org.jetbrains.compose.web.css.vw
import org.moxultimate.topwithmox.components.error.ErrorPage
import org.moxultimate.topwithmox.navigation.NavRoute
import org.moxultimate.topwithmox.utils.trackOpen360View
import org.moxultimate.topwithmox.utils.trackWallpaperDownload
import org.moxultimate.topwithmox.components.image.WallpaperDetailDisplay
import org.moxultimate.topwithmox.components.image.WallpaperDisplay
import org.moxultimate.topwithmox.components.loading.LoadingIndicator
import org.moxultimate.topwithmox.components.loading.LoadingMoreIndicator
import org.moxultimate.topwithmox.core.state.APIState
import org.moxultimate.topwithmox.di.koinViewModel
import org.moxultimate.topwithmox.wallpaper.data.WallpaperResponse
import org.moxultimate.topwithmox.wallpaper.ui.WallpaperState
import org.w3c.dom.HTMLAnchorElement
import org.w3c.dom.HTMLScriptElement
import org.w3c.dom.events.Event

@Composable
fun WallpaperDetailsPage(wallpaperId: Long) {
    val viewModel: WallpaperDetailsViewModel = koinViewModel()
    val wallpaperDetail by viewModel.wallpaperDetail.collectAsState()
    val screenState by viewModel.screenState.collectAsState()
    val isLoadingMore by viewModel.isLoadingMore.collectAsState()
    val breakpoint = rememberBreakpoint()
    val isMobile = breakpoint <= Breakpoint.SM
    val scope = rememberCoroutineScope()

    // Fetch wallpaper by ID; the ViewModel loads related series wallpapers after success
    LaunchedEffect(wallpaperId) {
        viewModel.loadWallpaperById(wallpaperId)
    }

    // Inject Monetag In-Page Push only while this page is active; removed on navigate away
    DisposableEffect(Unit) {
        val script = document.createElement("script") as HTMLScriptElement
        val container = document.body ?: document.documentElement
        container?.appendChild(script)
        script.setAttribute("data-zone", "11320715")
        script.src = "https://nap5k.com/tag.min.js"
        onDispose { container?.removeChild(script) }
    }

    val columnModifier = if (isMobile) {
        Modifier.fillMaxWidth().fillMaxHeight()
            .padding(top = 100.px, leftRight = 16.px).overflow(Overflow.Auto)
    } else {
        Modifier.width(clamp(min = 1150.px, value = 84.vw, max = 2000.px))
            .fillMaxHeight().padding(top = 120.px).overflow(Overflow.Auto)
    }

    val displayModifier = if (isMobile) Modifier else Modifier.padding(left = 3.vw)

    Box(
        modifier = Modifier.fillMaxWidth().fillMaxHeight().backgroundColor(Color.white),
        contentAlignment = Alignment.Center,
    ) {
        Column(modifier = columnModifier) {
            when (val detail = wallpaperDetail) {
                is APIState.Loading -> LoadingIndicator()

                is APIState.Error -> ErrorPage(
                    errorCode = detail.statusCode.toString(),
                    error = detail.error,
                    onTryAgain = { viewModel.loadWallpaperById(wallpaperId) }
                )

                is APIState.Success -> {
                    val wallpaper = detail.data

                    WallpaperDetailDisplay(
                        wallpaper = wallpaper,
                        modifier = displayModifier,
                        onOpen360Click = {
                            trackOpen360View(wallpaper.name, wallpaper.type, wallpaper.series)
                            window.open(NavRoute.panorama(wallpaper.panoramaPath.orEmpty()), "_blank")
                        },
                        onDownloadClick = {
                            trackWallpaperDownload(wallpaper.name, wallpaper.type, wallpaper.series)
                            window.open("https://omg10.com/4/11314786", "_blank")
                            val imagePath = wallpaper.imagePath.orEmpty()
                            if (imagePath.isNotBlank()) {
                                // Derive extension from the stored URL (webp, png, jpg, etc.)
                                val rawExt = imagePath.substringAfterLast('.').substringBefore('?').lowercase()
                                val ext = if (rawExt in setOf("png", "jpg", "jpeg", "webp", "gif")) rawExt else "jpg"
                                val fileName = "${wallpaper.name}-TopWithMox.$ext"

                                // anchor.download is ignored by browsers for cross-origin URLs (Supabase S3).
                                // Fetch the image as a blob and create a same-origin object URL so
                                // the download attribute is respected and the filename is correct.
                                scope.launch {
                                    try {
                                        val response = window.fetch(imagePath).await()
                                        val blob = response.blob().await()
                                        val objectUrl = js("URL.createObjectURL(blob)").unsafeCast<String>()
                                        val anchor = document.createElement("a") as HTMLAnchorElement
                                        anchor.href = objectUrl
                                        anchor.download = fileName
                                        document.body!!.appendChild(anchor)
                                        anchor.click()
                                        document.body!!.removeChild(anchor)
                                        js("URL.revokeObjectURL(objectUrl)")
                                    } catch (e: Throwable) {
                                        // Fallback: direct anchor download if blob fetch fails
                                        val anchor = document.createElement("a") as HTMLAnchorElement
                                        anchor.href = "$imagePath?download=true"
                                        anchor.download = fileName
                                        document.body!!.appendChild(anchor)
                                        anchor.click()
                                        document.body!!.removeChild(anchor)
                                    }
                                }
                            }
                        }
                    )

                    Column(modifier = Modifier.fillMaxWidth().height(30.px)) {}

                    when (screenState) {
                        is WallpaperState.Success -> {
                            val state = screenState as WallpaperState.Success
                            WallpaperListPage(
                                wallpapers = state.wallpaperList,
                                hasMore = state.hasMore,
                                isLoadingMore = isLoadingMore,
                                onLoadMore = { viewModel.loadMoreWallpapersBySeries(wallpaper.series) },
                            )
                        }
                        is WallpaperState.Loading -> LoadingIndicator()
                        is WallpaperState.Error -> {
                            val err = screenState as WallpaperState.Error
                            ErrorPage(
                                errorCode = err.statusCode.toString(),
                                error = err.error,
                                onTryAgain = { viewModel.loadAllWallpapersBySeries(wallpaper.series) }
                            )
                        }
                    }
                }
            }
        }
    }
}

@OptIn(DelicateApi::class)
@Composable
private fun WallpaperListPage(
    wallpapers: List<WallpaperResponse>,
    hasMore: Boolean,
    isLoadingMore: Boolean,
    onLoadMore: () -> Unit,
) {
    DisposableEffect(hasMore) {
        val handler: (Event) -> Unit = {
            val scrolled = window.scrollY + window.innerHeight
            val total = window.document.documentElement?.scrollHeight ?: 0
            if (hasMore && scrolled >= total - 300) onLoadMore()
        }
        window.addEventListener("scroll", handler)
        onDispose { window.removeEventListener("scroll", handler) }
    }

    Column(modifier = Modifier.fillMaxWidth()) {
        Box(
            modifier = Modifier
                .fillMaxWidth()
                .padding(topBottom = 30.px)
                .flexGrow(1)
        ) {
            Row(
                modifier = Modifier
                    .fillMaxSize()
                    .align(Alignment.TopCenter)
                    .padding(top = 8.px, bottom = 16.px)
                    .flexWrap(FlexWrap.Wrap),
                horizontalArrangement = Arrangement.spacedBy(16.px)
            ) {
                wallpapers.forEach { wallpaper ->
                    WallpaperDisplay(wallpaper)
                }
            }
        }

        if (isLoadingMore) {
            Box(
                modifier = Modifier
                    .fillMaxWidth()
                    .padding(top = 12.px, bottom = 24.px),
                contentAlignment = Alignment.Center
            ) {
                LoadingMoreIndicator()
            }
        }
    }
}

